import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, RefreshCw, ToggleLeft, ToggleRight } from "lucide-react";

const providers = [
  { id: "google", name: "Google Workspace", color: "bg-red-50 text-red-600" },
  { id: "microsoft", name: "Microsoft 365", color: "bg-blue-50 text-blue-600" },
  { id: "lark", name: "Lark / Feishu", color: "bg-indigo-50 text-indigo-600" },
  { id: "icloud", name: "iCloud", color: "bg-gray-50 text-gray-600" },
];

export default function CalendarAccounts() {
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [selectedWs, setSelectedWs] = useState<string>("");

  useEffect(() => {
    api.get("/api/v1/workspaces").then((ws) => {
      setWorkspaces(ws);
      if (ws.length > 0) setSelectedWs(ws[0].id.toString());
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedWs) return;
    api.get(`/api/v1/calendars?workspaceId=${selectedWs}`).then(setAccounts).catch(() => {});
  }, [selectedWs]);

  const handleConnect = async (provider: string) => {
    if (!selectedWs) { toast.error("Select a workspace first"); return; }
    try {
      const data = await api.get(`/api/v1/calendars/connect/${provider}?workspaceId=${selectedWs}&redirectUri=${encodeURIComponent(window.location.origin + "/api/v1/calendars/callback/" + provider)}`);
      if (data.url && !data.url.includes("PLACEHOLDER")) {
        window.location.href = data.url;
      } else {
        toast.info(`${provider} integration requires app registration. Configure credentials in environment variables.`);
      }
    } catch (e: any) { toast.error(e.message); }
  };

  const handleToggleSync = async (id: number, enabled: boolean) => {
    try {
      await api.post(`/api/v1/calendars/${id}/toggle-sync`, { enabled });
      setAccounts(accounts.map(a => a.id === id ? { ...a, sync_enabled: enabled ? 1 : 0 } : a));
      toast.success(enabled ? "Sync enabled" : "Sync disabled");
    } catch (e: any) { toast.error(e.message); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Disconnect this calendar?")) return;
    try { await api.delete(`/api/v1/calendars/${id}`); setAccounts(accounts.filter(a => a.id !== id)); toast.success("Disconnected"); }
    catch (e: any) { toast.error(e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Calendar Accounts</h1>
        {workspaces.length > 0 && (
          <select value={selectedWs} onChange={(e) => setSelectedWs(e.target.value)} className="px-3 py-2 rounded-lg border border-input bg-background text-sm">
            {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        )}
      </div>

      {/* Connected accounts */}
      {accounts.length > 0 && (
        <div className="space-y-2 mb-8">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Connected</h2>
          {accounts.map(a => {
            const p = providers.find(p => p.id === a.provider);
            return (
              <div key={a.id} className="bg-card rounded-xl border border-border p-4 flex items-center gap-4">
                <div className={`px-2.5 py-1 rounded-md text-xs font-medium ${p?.color || "bg-gray-50 text-gray-600"}`}>{p?.name || a.provider}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{a.account_email || a.account_label || "Connected"}</p>
                  <p className="text-xs text-muted-foreground">Status: {a.sync_status || "idle"} · Last sync: {a.last_sync_at || "never"}</p>
                </div>
                <button onClick={() => handleToggleSync(a.id, !a.sync_enabled)} className="p-1.5 rounded-lg hover:bg-accent">
                  {a.sync_enabled ? <ToggleRight className="w-5 h-5 text-primary" /> : <ToggleLeft className="w-5 h-5 text-muted-foreground" />}
                </button>
                <button onClick={() => handleDelete(a.id)} className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            );
          })}
        </div>
      )}

      {/* Connect new */}
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Connect Provider</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {providers.map(p => (
          <button key={p.id} onClick={() => handleConnect(p.id)} className="bg-card rounded-xl border border-border p-5 text-left hover:shadow-md transition-shadow">
            <div className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium mb-3 ${p.color}`}>{p.name}</div>
            <p className="text-sm text-muted-foreground">Connect your {p.name} calendar</p>
          </button>
        ))}
      </div>
    </div>
  );
}
